import { apiClient, handleApiSuccess, TokenManager } from './api';
import { AuthUser, LoginForm, SignUpForm } from '../types';
import { STORAGE_KEYS } from '../utils/config';

/**
 * Authentication Service
 * Handles all authentication-related API calls
 */
export class AuthService {
  /**
   * Login user with email and password
   */
  static async login(credentials: LoginForm): Promise<{ user?: AuthUser; token?: string; emailVerified?: boolean; data?: any }> {
    const response = await apiClient.post('/auth/login', credentials);

    // For login, we need to check the full response, not just response.data
    // because emailVerified is at the top level
    const fullResponse = response;

    // Check if email verification is required
    if (fullResponse.emailVerified === false && fullResponse.data?.requiresVerification) {
      // Return the verification response without storing token
      return {
        emailVerified: false,
        data: fullResponse.data
      };
    }

    // Use handleApiSuccess for successful login
    const data = handleApiSuccess(response);

    // Store token and user data for successful login
    TokenManager.setToken(data.token);
    this.storeUserData(data.user);

    return data;
  }

  /**
   * Register new user with profile picture support
   */
  static async register(userData: SignUpForm): Promise<{ user: AuthUser; token: string }> {
    // Check if profilePicture is a File (image upload) or string (emoji avatar)
    const isFileUpload = userData.profilePicture instanceof File;

    let response;
    if (isFileUpload) {
      // Create FormData for multipart upload if profile picture is a file
      const formData = new FormData();
      formData.append('name', userData.name);
      formData.append('email', userData.email);
      formData.append('password', userData.password);
      formData.append('role', userData.role);
      formData.append('collegeId', userData.collegeId);
      formData.append('branch', userData.branch);
      formData.append('mobileNumber', userData.mobileNumber);

      if (userData.rollNumber) {
        formData.append('rollNumber', userData.rollNumber);
      }
      if (userData.linkedin) {
        formData.append('linkedin', userData.linkedin);
      }
      if (userData.github) {
        formData.append('github', userData.github);
      }
      if (userData.website) {
        formData.append('website', userData.website);
      }
      if (userData.profilePicture) {
        formData.append('image', userData.profilePicture);
      }

      response = await apiClient.post('/auth/register', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } else {
      // Send as JSON if profilePicture is a string (emoji avatar)
      const requestData = {
        name: userData.name,
        email: userData.email,
        password: userData.password,
        role: userData.role,
        collegeId: userData.collegeId,
        branch: userData.branch,
        mobileNumber: userData.mobileNumber,
        rollNumber: userData.rollNumber || undefined,
        linkedin: userData.linkedin || undefined,
        github: userData.github || undefined,
        website: userData.website || undefined,
        profilePicture: userData.profilePicture || undefined,
      };

      response = await apiClient.post('/auth/register', requestData);
    }

    const data = handleApiSuccess(response);

    // Store token and user data
    TokenManager.setToken(data.token);
    this.storeUserData(data.user);

    return data;
  }

  /**
   * Get current user information
   */
  static async getCurrentUser(): Promise<AuthUser> {
    const response = await apiClient.get('/auth/me');
    const data = handleApiSuccess(response);

    // Update stored user data
    this.storeUserData(data.user);

    return data.user;
  }

  /**
   * Logout user
   */
  static logout(): void {
    TokenManager.removeToken();
    this.removeUserData();

    // Redirect to login page
    window.location.href = '/login';
  }

  /**
   * Check if user is authenticated
   */
  static isAuthenticated(): boolean {
    return TokenManager.isAuthenticated();
  }

  /**
   * Get stored user data
   */
  static getStoredUserData(): AuthUser | null {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing stored user data:', error);
      return null;
    }
  }

  /**
   * Store user data in localStorage
   * Only store minimal data - full data fetched from JWT
   */
  private static storeUserData(user: AuthUser): void {
    // Only store essential data
    const minimalUser = {
      id: user.id,
      name: user.name,
      role: user.role,
    };
    localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(minimalUser));
  }

  /**
   * Remove user data from localStorage
   */
  private static removeUserData(): void {
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Verify user email (if implemented)
   */
  static async verifyEmail(token: string): Promise<void> {
    const response = await apiClient.post('/auth/verify-email', { token });
    handleApiSuccess(response);
  }

  /**
   * Request password reset (if implemented)
   */
  static async requestPasswordReset(email: string): Promise<void> {
    const response = await apiClient.post('/auth/forgot-password', { email });
    handleApiSuccess(response);
  }

  /**
   * Reset password (if implemented)
   */
  static async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      password: newPassword,
    });
    handleApiSuccess(response);
  }

  /**
   * Change password
   */
  static async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const response = await apiClient.put('/auth/change-password', {
      currentPassword,
      newPassword,
    });
    handleApiSuccess(response);
  }

  /**
   * Refresh auth token (if implemented)
   */
  static async refreshToken(): Promise<string> {
    const refreshToken = TokenManager.getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await apiClient.post('/auth/refresh', {
      refreshToken,
    });
    const data = handleApiSuccess(response);

    // Update stored token
    TokenManager.setToken(data.token);
    if (data.refreshToken) {
      TokenManager.setRefreshToken(data.refreshToken);
    }

    return data.token;
  }

  /**
   * Initialize auth state from storage
   */
  static initializeAuth(): { user: AuthUser | null; isAuthenticated: boolean } {
    const isAuthenticated = this.isAuthenticated();
    const user = isAuthenticated ? this.getStoredUserData() : null;

    return { user, isAuthenticated };
  }

  /**
   * Validate user session
   */
  static async validateSession(): Promise<boolean> {
    if (!this.isAuthenticated()) {
      return false;
    }

    try {
      await this.getCurrentUser();
      return true;
    } catch (error) {
      // Invalid token or network error
      this.logout();
      return false;
    }
  }
}

export default AuthService;